/**
 * CalendarSidebar - Sidebar do Calendário Global
 *
 * Lista calendários disponíveis (checkbox individual + cor),
 * filtro por tipo de fonte, ação de full-sync e CRUD de calendários
 * (criar, editar, tornar primário, ativar/desativar, excluir).
 *
 * RELEASE:
 *   2026-09-17 v1.3.0 - "Filtrar por fonte" passa a listar apenas as fontes dos
 *                       módulos contratados (useAvailableCalendarSources)
 *   2026-08-04 v1.2.0 - Ícone do Google Agenda em calendários sincronizados
 *   2026-06-24 v1.1.0 - Botão "+" e menu de ações por calendário (CRUD)
 *   2026-06-24 v1.0.0 - Versão inicial (Fase 2)
 */

import { useMemo, useState } from 'react';
import googleCalendarIcon from '@/assets/google-calendar-icon.png';

import {
  Calendar as CalendarIcon,
  RefreshCw,
  Loader2,
  Shield,
  Plus,
  Pencil,
  Star,
  Power,
  Trash2,
  MoreVertical,
  Settings2,
  HelpCircle,
  Users,
} from 'lucide-react';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { Checkbox } from '@/components/ui/checkbox';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';



import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { useCalendars, type UserCalendar } from '@/hooks/useCalendars';
import type { CalendarEventSourceType } from '@/hooks/useCalendarUnifiedEvents';
import { CalendarPermissionsMatrix } from './CalendarPermissionsMatrix';
import { CalendarFormDialog } from './CalendarFormDialog';
import { CalendarMiniCalendar } from './CalendarMiniCalendar';
import { GoogleSyncStatusBanner } from './GoogleSyncStatusBanner';


import { SOURCE_LABELS, SOURCE_TOOLTIPS, SOURCE_ICON_META } from './sourceMeta';
// v1.3.0: fonte única das fontes disponíveis (gate por módulo contratado)
import { useAvailableCalendarSources } from '@/hooks/useAvailableCalendarSources';



interface CalendarSidebarProps {
  calendars: UserCalendar[];
  selectedCalendarIds: string[];
  onToggleCalendar: (calendarId: string) => void;
  selectedSources: CalendarEventSourceType[];
  onToggleSource: (source: CalendarEventSourceType) => void;
  isLoading?: boolean;
  onFullSync?: (calendarId: string) => void;
  syncingCalendarId?: string | null;
  /** Abre o modal de configurações de calendários (substitui navegação). */
  onOpenSettings?: () => void;
  /** Datas com eventos para o mini calendário (já agregadas). */
  miniEventDates?: Date[];
  /** Data atualmente selecionada no mini calendário. */
  miniSelectedDate?: Date;
  /** Disparado ao clicar em um dia do mini calendário. */
  onMiniSelectDate?: (date: Date | undefined) => void;
  /** Disparado ao trocar o mês no mini calendário. */
  onMiniMonthChange?: (date: Date) => void;
  /** v1.2.0: mostrar eventos concluídos/cancelados (default true) */
  includeFinished?: boolean;
  /** v1.2.0: callback quando o toggle "Mostrar concluídos" muda */
  onToggleIncludeFinished?: (value: boolean) => void;
}

export function CalendarSidebar({
  calendars,
  selectedCalendarIds,
  onToggleCalendar,
  selectedSources,
  onToggleSource,
  isLoading,
  onFullSync,
  syncingCalendarId,
  onOpenSettings,
  miniEventDates = [],
  miniSelectedDate,
  onMiniSelectDate,
  onMiniMonthChange,
  includeFinished = true,
  onToggleIncludeFinished,
}: CalendarSidebarProps) {
  // v1.3.0: fontes exibidas respeitam os módulos contratados pelo tenant e a
  // existência de integração Google (evita opções que o usuário não possui).
  const hasGoogleIntegration = useMemo(
    () => calendars.some((c) => !!c.google_integration_id),
    [calendars],
  );
  const allSources = useAvailableCalendarSources({ hasGoogleIntegration });

  // Estados de diálogos
  const [permissionsCalendar, setPermissionsCalendar] = useState<UserCalendar | null>(null);
  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState<UserCalendar | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<UserCalendar | null>(null);

  // Hook só para mutations rápidas (toggle primário/ativo, excluir)
  const { update, remove, isRemoving } = useCalendars();

  /** Abre o diálogo de criação */
  const handleCreate = () => {
    setEditing(null);
    setFormOpen(true);
  };

  /** Abre o diálogo de edição */
  const handleEdit = (cal: UserCalendar) => {
    setEditing(cal);
    setFormOpen(true);
  };

  /** Marca como primário (silenciosamente) */
  const handleSetPrimary = async (cal: UserCalendar) => {
    await update({ id: cal.id, is_primary: true });
  };

  /** Alterna ativo/inativo */
  const handleToggleActive = async (cal: UserCalendar) => {
    await update({ id: cal.id, is_active: !cal.is_active });
  };

  /** Confirma exclusão */
  const handleConfirmDelete = async () => {
    if (!deleteTarget) return;
    try {
      await remove(deleteTarget.id);
      setDeleteTarget(null);
    } catch {
      setDeleteTarget(null);
    }
  };

  /**
   * v1.2.0: separa calendários próprios dos que o usuário só enxerga por
   * bypass (admin de tenant ou compartilhamento). Vendedor comum só possui
   * calendários próprios, então a segunda seção nem chega a renderizar.
   */
  const { myCalendars, teamCalendars } = useMemo(() => {
    const mine: UserCalendar[] = [];
    const team: UserCalendar[] = [];
    for (const c of calendars) {
      (c.is_owner ? mine : team).push(c);
    }
    return { myCalendars: mine, teamCalendars: team };
  }, [calendars]);

  /**
   * Rótulo do dono do calendário (para a seção "Calendários da equipe").
   * Fallback: full_name -> email -> UUID truncado 8 chars (padrão do projeto).
   */
  const ownerLabel = (cal: UserCalendar): string => {
    if (cal.owner_full_name && cal.owner_full_name.trim()) return cal.owner_full_name;
    if (cal.owner_email && cal.owner_email.trim()) return cal.owner_email;
    return cal.owner_user_id.slice(0, 8).toUpperCase();
  };

  /**
   * Renderiza um item da lista de calendários. Compartilhado entre as duas
   * seções (meus / equipe). `showOwner` exibe o nome do dono abaixo do nome.
   */
  const renderCalendarItem = (cal: UserCalendar, showOwner: boolean) => {
    const checked = selectedCalendarIds.includes(cal.id);
    const isGoogle = !!cal.google_integration_id;
    const isSyncing = syncingCalendarId === cal.id;
    const canManage = cal.user_permission === 'manage';
    return (
      <li
        key={cal.id}
        className="flex items-center gap-2 px-1.5 py-1 rounded-md hover:bg-muted/50 group"
      >
        <Checkbox
          id={`cal-${cal.id}`}
          checked={checked}
          onCheckedChange={() => onToggleCalendar(cal.id)}
          className="h-3.5 w-3.5"
        />
        <span
          className="h-2.5 w-2.5 rounded-sm shrink-0 border border-border/50"
          style={{ backgroundColor: cal.color }}
          aria-hidden
        />
        <Label
          htmlFor={`cal-${cal.id}`}
          className="flex-1 min-w-0 cursor-pointer flex flex-col leading-tight"
          title={showOwner ? `${cal.name} — ${ownerLabel(cal)}` : cal.name}
        >
          <span className="text-xs truncate flex items-center gap-1">
            {/* v: sinaliza visualmente calendários sincronizados com o Google */}
            {isGoogle && (
              <img
                src={googleCalendarIcon}
                alt="Sincronizado com Google Agenda"
                title="Sincronizado com Google Agenda"
                className="h-3 w-3 shrink-0"
              />
            )}
            <span className="truncate">{cal.name}</span>
            {cal.is_primary && (
              <Star className="h-3 w-3 text-amber-500 fill-amber-500 shrink-0" />
            )}
          </span>

          {showOwner && (
            <span className="text-[10px] text-muted-foreground truncate">
              {ownerLabel(cal)}
            </span>
          )}
        </Label>

        {isGoogle && onFullSync && canManage && (
          <Button
            size="icon"
            variant="ghost"
            className="h-5 w-5 opacity-0 group-hover:opacity-100 transition-opacity"
            onClick={() => onFullSync(cal.id)}
            disabled={isSyncing}
            title="Sincronizar agora"
          >
            {isSyncing ? (
              <Loader2 className="h-3 w-3 animate-spin" />
            ) : (
              <RefreshCw className="h-3 w-3" />
            )}
          </Button>
        )}

        {/* Menu de ações — só faz sentido quando o usuário tem manage */}
        {canManage && (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                size="icon"
                variant="ghost"
                className="h-5 w-5 opacity-0 group-hover:opacity-100 transition-opacity"
                title="Mais ações"
              >
                <MoreVertical className="h-3 w-3" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-48">
              <DropdownMenuItem onClick={() => handleEdit(cal)}>
                <Pencil className="h-3.5 w-3.5 mr-2" />
                Editar
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => setPermissionsCalendar(cal)}>
                <Shield className="h-3.5 w-3.5 mr-2" />
                Compartilhamento
              </DropdownMenuItem>
              <DropdownMenuItem
                onClick={() => handleSetPrimary(cal)}
                disabled={cal.is_primary}
              >
                <Star className="h-3.5 w-3.5 mr-2" />
                Tornar primário
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => handleToggleActive(cal)}>
                <Power className="h-3.5 w-3.5 mr-2" />
                {cal.is_active ? 'Desativar' : 'Ativar'}
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem
                onClick={() => setDeleteTarget(cal)}
                className="text-destructive focus:text-destructive"
              >
                <Trash2 className="h-3.5 w-3.5 mr-2" />
                Excluir
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        )}
      </li>
    );
  };

  return (
    <aside
      className="group/sidebar w-full lg:w-64 shrink-0 space-y-2.5 lg:h-full lg:min-h-0 lg:overflow-y-auto pr-0.5 [&::-webkit-scrollbar]:w-1.5 [&::-webkit-scrollbar-thumb]:bg-transparent [&::-webkit-scrollbar-thumb]:rounded-full hover:[&::-webkit-scrollbar-thumb]:bg-border [&::-webkit-scrollbar-thumb]:transition-colors"
    >
      {/* Mini calendário (v1.1.0) */}
      {onMiniSelectDate && (
        <CalendarMiniCalendar
          eventDates={miniEventDates}
          selectedDate={miniSelectedDate}
          onSelectDate={onMiniSelectDate}
          onMonthChange={onMiniMonthChange}
        />
      )}

      {/* v1.3.0: status real da sincronização com o Google Agenda */}
      <GoogleSyncStatusBanner calendars={calendars} />

      {/* Bloco: Meus calendários (calendários de propriedade do usuário logado) */}

      <div className="rounded-md border bg-card">
        <div className="flex items-center gap-1.5 px-2.5 py-1.5 border-b">
          <CalendarIcon className="h-3 w-3 text-muted-foreground" />
          <h2 className="text-[10px] font-semibold uppercase tracking-wide text-muted-foreground flex-1">
            Meus calendários
          </h2>
          <Button
            size="icon"
            variant="ghost"
            className="h-5 w-5"
            onClick={handleCreate}
            title="Novo calendário"
          >
            <Plus className="h-3 w-3" />
          </Button>
          <Button
            size="icon"
            variant="ghost"
            className="h-5 w-5"
            title="Configurações de calendários"
            onClick={() => onOpenSettings?.()}
          >
            <Settings2 className="h-3 w-3" />
          </Button>
        </div>

        <div className="p-1.5">
          {isLoading && (
            <p className="text-xs text-muted-foreground px-1.5 py-1">Carregando…</p>
          )}

          {!isLoading && myCalendars.length === 0 && teamCalendars.length === 0 && (
            <p className="text-xs text-muted-foreground px-1.5 py-1">
              Nenhum calendário disponível ainda.
            </p>
          )}

          {!isLoading && myCalendars.length === 0 && teamCalendars.length > 0 && (
            <p className="text-xs text-muted-foreground px-1.5 py-1">
              Você ainda não possui calendários próprios.
            </p>
          )}

          <ul className="space-y-0.5">
            {myCalendars.map((cal) => renderCalendarItem(cal, false))}
          </ul>
        </div>
      </div>

      {/* Bloco: Calendários da equipe (v1.2.0) — só aparece quando o usuário
          tem visibilidade sobre calendários de outros donos (admin do tenant
          ou compartilhamento explícito). Para vendedor comum, fica oculto. */}
      {teamCalendars.length > 0 && (
        <div className="rounded-md border bg-card">
          <div className="flex items-center gap-1.5 px-2.5 py-1.5 border-b">
            <Users className="h-3 w-3 text-muted-foreground" />
            <h2 className="text-[10px] font-semibold uppercase tracking-wide text-muted-foreground flex-1">
              Calendários da equipe
            </h2>
          </div>
          <div className="p-1.5">
            <ul className="space-y-0.5">
              {teamCalendars.map((cal) => renderCalendarItem(cal, true))}
            </ul>
          </div>
        </div>
      )}


      {/* Bloco: Filtrar por fonte */}
      <div className="rounded-md border bg-card">
        <div className="px-2.5 py-1.5 border-b">
          <h2 className="text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">
            Filtrar por fonte
          </h2>
        </div>
        <ul className="p-1.5 space-y-0.5">
          {allSources.map((src) => {
            const meta = SOURCE_ICON_META[src];
            const SourceIcon = meta?.Icon;
            const iconColor = meta ? `hsl(var(${meta.cssVar}))` : undefined;
            return (
              <li
                key={src}
                className="flex items-center gap-2 px-1.5 py-1 rounded-md hover:bg-muted/50"
              >
                <Checkbox
                  id={`src-${src}`}
                  checked={selectedSources.includes(src)}
                  onCheckedChange={() => onToggleSource(src)}
                  className="h-3.5 w-3.5"
                />
                {/* Ícone da fonte (paridade visual com a lista de eventos) */}
                {SourceIcon && (
                  <SourceIcon
                    className="h-3.5 w-3.5 shrink-0"
                    style={{ color: iconColor }}
                    aria-hidden
                  />
                )}
                <Label
                  htmlFor={`src-${src}`}
                  className="text-xs cursor-pointer"
                >
                  {SOURCE_LABELS[src]}
                </Label>
                {/* Popover de ajuda clicável — fica logo após o label, não alinhado à direita */}
                <Popover>
                  <PopoverTrigger asChild>
                    <button
                      type="button"
                      aria-label={`Ajuda: ${SOURCE_LABELS[src]}`}
                      className="flex h-4 w-4 items-center justify-center rounded-full text-muted-foreground/50 hover:text-foreground hover:bg-muted focus:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                    >
                      <HelpCircle className="h-3 w-3" />
                    </button>
                  </PopoverTrigger>
                  <PopoverContent side="right" align="center" className="w-64 text-xs leading-relaxed">
                    {SOURCE_TOOLTIPS[src]}
                  </PopoverContent>
                </Popover>
              </li>
            );
          })}
        </ul>

        {/* v1.2.0: toggle "Mostrar concluídos/cancelados" */}
        <div className="border-t px-2.5 py-2">
          <label className="flex items-center gap-2 cursor-pointer select-none">
            <Checkbox
              id="include-finished"
              checked={includeFinished}
              onCheckedChange={(v) => onToggleIncludeFinished?.(!!v)}
              className="h-3.5 w-3.5"
            />
            <span className="text-xs text-foreground">
              Mostrar concluídos/cancelados
            </span>
          </label>
          <p className="mt-1 text-[10px] text-muted-foreground leading-snug pl-6">
            Eventos finalizados aparecem com traço no calendário e na lista.
          </p>
        </div>

      </div>

      {/* Diálogos */}
      <CalendarPermissionsMatrix
        calendar={permissionsCalendar}
        open={!!permissionsCalendar}
        onOpenChange={(open) => {
          if (!open) setPermissionsCalendar(null);
        }}
      />

      <CalendarFormDialog
        open={formOpen}
        onOpenChange={setFormOpen}
        calendar={editing}
      />

      <AlertDialog open={!!deleteTarget} onOpenChange={(open) => !open && setDeleteTarget(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Excluir calendário?</AlertDialogTitle>
            <AlertDialogDescription>
              O calendário <strong>{deleteTarget?.name}</strong> e todos os seus eventos manuais
              serão excluídos permanentemente. Esta ação não pode ser desfeita.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={isRemoving}>Cancelar</AlertDialogCancel>
            <AlertDialogAction
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              onClick={handleConfirmDelete}
              disabled={isRemoving}
            >
              {isRemoving && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              Excluir
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </aside>
  );
}
